import React, {
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    MdKeyboardArrowRight,
    MdStar,
} from 'react-icons/md';
import { IS_FRAME_SERVICE } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/frameService';
import { dispatchTrackingEvent } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/tracking';
import WishlistWidget from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/WishlistWidget';
import HorizontalScrollArrows from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/HorizontalScrollArrows';

const ITEM_WIDTH_PX = 300;
const ITEMS_GAP_PX = 16;
const ITEM_TOTAL_WIDTH_PX = ITEM_WIDTH_PX + ITEMS_GAP_PX;
const SCROLL_ANIMATION_DURATION_MS = 500;

// Promjene u HTML strukturi potrebno primijeniti i na "featuredAccommodation.html.twig" jer koriste isti css.
export default function StandardOfferAccommodation({
    accommodation,
    initialUserWishlist,
    openLoginModal,
    eventNames: { seeMoreClick, itemClick, itemView },
}) {
    const [itemsScrollableElement, setItemsScrollableElement] = useState(null);
    const standardOfferAccommodationRef = useRef(null);

    useEffect(() => {
        if (IS_FRAME_SERVICE) return undefined;
        const element = standardOfferAccommodationRef.current;
        if (!element) return undefined;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    dispatchTrackingEvent(itemView);
                    observer.unobserve(entry.target);
                }
            },
            {
                // Trigger-a se tek kad uđe sa 25% elementa.
                threshold: 0.25,
            },
        );
        observer.observe(element);

        return () => { observer.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLastMinuteItemSeeAll = () => {
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(seeMoreClick);
    };

    const handleLastMinuteItemTracking = () => {
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(itemClick);
    };

    return (
        <div ref={standardOfferAccommodationRef} className="standardOfferAccommodation">
            <a
                href={accommodation.hyperlink.redirectURL}
                title={accommodation.hyperlink.redirectURLTitle}
                className="featuredAccommodation--header"
                onClick={handleLastMinuteItemSeeAll}
                onAuxClick={(event) => {
                    if (event.button === 1) {
                        handleLastMinuteItemSeeAll();
                    }
                }}
            >
                <h2 className="featured-accommodation__title">{accommodation.title}</h2>
                <span className="featured-accommodation__header-line" />
                <span className="featuredAccommodation__itemsTotalCount">
                    {accommodation.totalNumberInOffer}
                </span>
                <span className="featuredAccommodation__header-see-more">
                    <span className="featuredAccommodation__header-see-more-text">
                        {accommodation.hyperlink.redirectURLTitle}
                    </span>
                    <MdKeyboardArrowRight className="featuredAccommodation__header-see-more-icon" />
                </span>
            </a>
            <div className="featuredAccommodation__items-container">
                <div
                    ref={(el) => {
                        if (el && el !== itemsScrollableElement) {
                            setItemsScrollableElement(el);
                        }
                    }}
                    className="featuredAccommodation__items"
                >
                    {accommodation.standardOffers.map((item) => (
                        <div
                            className="featuredAccommodation__items-item"
                            key={item.unitId}
                            data-id={item.unitId}
                        >
                            <WishlistWidget
                                unitsData={[
                                    {
                                        unitId: item.unitId,
                                        title: item.unitName,
                                    },
                                ]}
                                initialWishlistedUnits={
                                    initialUserWishlist
                                        ? (
                                            initialUserWishlist.find(
                                                (wishlistItem) => (
                                                    wishlistItem.units.includes(
                                                        item.unitId,
                                                    )
                                                ),
                                            )?.units ?? []
                                        )
                                        : null
                                }
                                openLoginModal={openLoginModal}
                            />
                            <a
                                href={item.unitHyperlink.redirectURL}
                                title={item.unitHyperlink.seoUnitTitle}
                                className="featuredAccommodation__card-wrap"
                                onClick={handleLastMinuteItemTracking}
                                onAuxClick={(event) => {
                                    if (event.button === 1) {
                                        handleLastMinuteItemTracking();
                                    }
                                }}
                            >
                                <img
                                    className="featured-accommodation__item-image"
                                    loading="lazy"
                                    src={item.image.sourceURL}
                                    srcSet={[
                                        item.image.sourceURL
                                            && `${item.image.sourceURL} 1x`,
                                        item.imageSourceURLMediumSizeAlternative
                                            && `${item.imageSourceURLMediumSizeAlternative} 2x`,
                                        item.imageSourceURLLargeSizeAlternative
                                            && `${item.imageSourceURLLargeSizeAlternative} 3x`,
                                    ]
                                        .filter(Boolean)
                                        .join(', ')}
                                    alt={item.image.altText}
                                    title={item.image.titleText}
                                />
                                <h3 className="featuredAccommodation__item-title">
                                    {item.unitHyperlink.unitTitle}
                                </h3>
                                <p className="featuredAccommodation__item-subtitle">
                                    {item.unitHyperlink.locationAndUnitName}
                                </p>
                                <div className="card-price">
                                    <p className="card-price-text">
                                        {item.priceFromString}
                                    </p>
                                    <MdKeyboardArrowRight className="card-price-icon" />
                                </div>
                                <div className="featured-accommodation__item-floating-left-side">
                                    {item.rating && (
                                        <span className="featured-accommodation__item-rating">
                                            <MdStar className="featured-accommodation__item-rating-icon" />
                                            {item.rating}
                                        </span>
                                    )}
                                    {item.hasActiveDiscount && (
                                        <span className="featured-accommodation__item-discount">
                                            %
                                        </span>
                                    )}
                                </div>
                            </a>
                        </div>
                    ))}
                </div>
            </div>
            <HorizontalScrollArrows
                scrollableElement={itemsScrollableElement}
                scrollAmountPx={ITEM_TOTAL_WIDTH_PX}
                durationMs={SCROLL_ANIMATION_DURATION_MS}
                alignRight={true}
            />
        </div>
    );
}
